import * as React from 'react';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { simpleDateTimeFormat } from '../../Helpers';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import { TApiMeetingEntity } from '../../types/Meeting';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { Theme } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import Chip from '@mui/material/Chip';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import Box from '@mui/material/Box';
import { Alert } from '@mui/material';
import { fetchWithAuthentication } from '../../actions/BaseAction';

interface IMeetingProps {
  meeting: TApiMeetingEntity;
}

const attendanceKey = (status: string): string =>
  status === 'NotApplicable' ? 'not_applicable' : status.toLowerCase();

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      marginTop: theme.spacing(3),
    },
    table: {
      width: '100%',
    },
    title: {
      flex: '1 1 100%',
      padding: theme.spacing(1, 2),
    },
    title2: {
      fontWeight: 'normal',
      padding: theme.spacing(3, 0),
    },
    minutes: {
      marginTop: theme.spacing(1),
      padding: theme.spacing(0, 1),
    },
  })
);

const Meeting: React.FC<IMeetingProps> = ({ meeting }): JSX.Element => {
  const { t } = useTranslation();
  const classes = useStyles({});

  const [expanded, setExpanded] = React.useState(true);
  const [meetingFile, setMeetingFile] = useState('*');

  useEffect(() => {
    if (meeting.file) {
      fetchWithAuthentication(meeting.file)
        .then(blob => {
          if (blob !== null) {
            setMeetingFile(URL.createObjectURL(blob));
          }
        })
        .catch(error => {throw error});
    }
  }, [meeting.file]);

  const handleChange = () => (event: ChangeEvent<{}>, expanded: boolean) => {
    setExpanded(expanded);
  };

  return (
    meeting && (
      <>
        {meeting.meeting.name && (
          <h2>{meeting.meeting.name as string}</h2>
        )}
        <Accordion expanded={expanded} onChange={handleChange()}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            style={{
              paddingLeft: 0,
            }}
          >
            <Typography className={classes.title} variant='h6'>
              {t('meetings.meeting_details')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails
            style={{
              paddingLeft: 0,
              paddingRight: 0,
            }}
          >
            <TableContainer>
              <Table className={classes.table} size='small'>
                <TableBody>
                  {meeting.meeting.details && (
                    <TableRow
                      style={{
                        verticalAlign: 'top',
                      }}
                    >
                      <TableCell component='th' scope='row'>
                        <Box fontWeight={'bold'}>{t('meetings.details')}</Box>
                      </TableCell>
                      <TableCell align='right'>
                        {meeting.meeting.details}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    style={{
                      verticalAlign: 'top',
                    }}
                  >
                    <TableCell component='th' scope='row'>
                      <Box fontWeight={'bold'}>{t('meetings.status')}</Box>
                    </TableCell>
                    <TableCell align='right'>
                      <Chip
                        style={{
                          backgroundColor: meeting.meeting.enabled
                            ? '#8dc63f'
                            : '#cc3f44',
                          color: '#fff',
                          textTransform: 'uppercase',
                        }}
                        size='small'
                        label={
                          meeting.meeting.enabled
                            ? t('meetings.status.open')
                            : t('meetings.status.closed')
                        }
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow
                    style={{
                      verticalAlign: 'top',
                    }}
                  >
                    <TableCell component='th' scope='row'>
                      <Box fontWeight={'bold'}>{t('meetings.organizer')}</Box>
                    </TableCell>
                    <TableCell align='right'>
                      {meeting.meeting.organizer}
                    </TableCell>
                  </TableRow>
                  {meeting.meeting.location && (
                    <TableRow
                      style={{
                        verticalAlign: 'top',
                      }}
                    >
                      <TableCell component='th' scope='row'>
                        <Box fontWeight={'bold'}>{t('meetings.location')}</Box>
                      </TableCell>
                      <TableCell align='right'>
                        {meeting.meeting.location}
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    style={{
                      verticalAlign: 'top',
                    }}
                  >
                    <TableCell component='th' scope='row'>
                      <Box fontWeight={'bold'}>
                        {t('meetings.date_and_time')}
                      </Box>
                    </TableCell>
                    <TableCell align='right'>
                      {simpleDateTimeFormat(meeting.meeting.startTime)}
                      {meeting.meeting.endTime
                        ? ' - ' + simpleDateTimeFormat(meeting.meeting.endTime)
                        : ''}
                    </TableCell>
                  </TableRow>
                  <TableRow
                    style={{
                      verticalAlign: 'top',
                      border: 0,
                    }}
                  >
                    <TableCell
                      component='th'
                      scope='row'
                      style={{
                        border: 0,
                      }}
                    >
                      <Box fontWeight={'bold'}>{t('meetings.attendees')}</Box>
                    </TableCell>
                    <TableCell
                      align='right'
                      style={{
                        border: 0,
                      }}
                    >
                      {meeting.meeting.attendees.map((obj, i) => {
                        return (
                          <React.Fragment key={i}>
                            {obj.name}
                            {obj.title ? ', ' + obj.title : ''}
                            {obj.status
                              ? ' (' +
                                t(
                                  'meetings.attendance.' +
                                    attendanceKey(obj.status)
                                ) +
                                ')'
                              : ''}
                            <br />
                          </React.Fragment>
                        );
                      })}
                    </TableCell>
                  </TableRow>
                  {meeting.meeting.organisations.length > 0 && (
                    <TableRow
                      style={{
                        verticalAlign: 'top',
                        border: 0,
                      }}
                    >
                      <TableCell
                        component='th'
                        scope='row'
                        style={{ border: 0 }}
                      >
                        <Box fontWeight={'bold'}>
                          {t('meetings.related_groups')}
                        </Box>
                      </TableCell>
                      <TableCell align='right' style={{ border: 0 }}>
                        {meeting.meeting.organisations.map((obj, i) => (
                          <React.Fragment key={i}>
                            {obj}
                            <br />
                          </React.Fragment>
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                  {meeting.meeting.relatedLocations.length > 0 && (
                    <TableRow
                      style={{
                        verticalAlign: 'top',
                        border: 0,
                      }}
                    >
                      <TableCell
                        component='th'
                        scope='row'
                        style={{ border: 0 }}
                      >
                        <Box fontWeight={'bold'}>
                          {t('meetings.related_locations')}
                        </Box>
                      </TableCell>
                      <TableCell align='right' style={{ border: 0 }}>
                        {meeting.meeting.relatedLocations.map((obj, i) => (
                          <React.Fragment key={i}>
                            {obj}
                            <br />
                          </React.Fragment>
                        ))}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {meeting.agenda && (
          <>
            <Typography className={classes.title2} variant='h6'>
              {t('meetings.agenda_and_minutes')}
            </Typography>

            <TableContainer>
              <Table className={classes.table} size='small'>
                <TableBody>
                  {meeting.agenda.map((obj, i) => {
                    const minutes = obj.minutes.trim();

                    return (
                      <TableRow
                        key={i}
                        style={{
                          verticalAlign: 'top',
                        }}
                      >
                        <TableCell>{i + 1}</TableCell>
                        <TableCell>
                          <b>{obj.name}</b>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: obj.description,
                            }}
                          />
                          {minutes && (
                            <Alert
                              severity={'info'}
                              icon={false}
                              className={classes.minutes}
                            >
                              <div
                                dangerouslySetInnerHTML={{
                                  __html: minutes,
                                }}
                              />
                            </Alert>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {meeting.file && (
          <>
            <Grid container className={classes.root} justifyContent="space-around">
              <Grid item xs={6}>
                <Button
                  type='button'
                  fullWidth
                  variant='contained'
                  color='primary'
                  href={meetingFile}
                  style={{
                    textTransform: 'uppercase',
                  }}
                  download
                >
                  {t('meetings.agenda_and_minutes')}
                </Button>
              </Grid>
            </Grid>
          </>
        )}
      </>
    )
  );
};

export default Meeting;
